import { MouseEventHandler } from "react";
import { Button, ButtonProps } from "@chakra-ui/react";
import { MutationResult } from "@apollo/client";
import { useBrandMutation } from "utils/apollo/hooks/brand";
import { gql, DocumentType } from "__generated__/gql";
import { asyncVoid } from "utils/asyncVoidHandler";
import PlusButton from "components/Shared/PlusButton";
import { useCurrentBrandId } from "utils/currentUserHooks";
import { assertBrandId, assertPresence } from "utils/assertions";
import { addNewRecordsWithFieldSpec } from "utils/apollo/cacheHelpers";
import useAsyncCompleteToast from "components/Shared/useAsyncCompleteToast";

const mutation = gql(/* GraphQL */ `
  mutation AddBrandRetailerButton($data: BrandRetailerInput!, $brandId: UUID!) {
    createBrandRetailer(input: { brandRetailer: $data }) {
      brandRetailer {
        id
        brandId
        retailerId
        retailer {
          id
          nameWithUniqueSuffix
          brandRetailerForBrand(brandId: $brandId) {
            id
          }
        }
        ...BrandRetailersTableRow
      }
    }
  }
`);

export function useAddBrandRetailer(
  retailerId: UUID,
): [
  MouseEventHandler<HTMLButtonElement>,
  MutationResult<DocumentType<typeof mutation>>,
] {
  const brandId = useCurrentBrandId();

  const [addBrandRetailer, mutationResult] = useBrandMutation(mutation, {
    variables: brandId ? { data: { brandId, retailerId }, brandId } : undefined,
    update: (cache, result) => {
      const newBcr = result.data?.createBrandRetailer?.brandRetailer;
      assertPresence(newBcr);
      addNewRecordsWithFieldSpec({
        cache,
        baseFieldName: "brandRetailers",
        newObjs: [newBcr],
        getArgs: (existing) => ({ ...existing, condition: { brandId } }),
      });
    },
  });

  const { showSuccessToast, showErrorToast } = useAsyncCompleteToast();

  const handleClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    assertBrandId(brandId);
    asyncVoid(
      addBrandRetailer()
        .then((result) =>
          showSuccessToast(
            <>
              <b>
                “
                {
                  result.data?.createBrandRetailer?.brandRetailer?.retailer
                    ?.nameWithUniqueSuffix
                }
                ”
              </b>{" "}
              added successfully
            </>,
          ),
        )
        .catch(showErrorToast),
    );
  };

  return [handleClick, mutationResult];
}

type PlusButtonProps = {
  retailerId: UUID;
};

export function AddRetailerPlusButton({ retailerId }: PlusButtonProps) {
  const [handleClick, { loading }] = useAddBrandRetailer(retailerId);
  return (
    <PlusButton
      label="Add retailer"
      onClick={handleClick}
      isLoading={loading}
    />
  );
}

type Props = ButtonProps & {
  retailerId: UUID;
};

export default function AddRetailerButton({ retailerId, ...rest }: Props) {
  const [handleClick, { loading }] = useAddBrandRetailer(retailerId);
  return <Button {...rest} onClick={handleClick} isLoading={loading} />;
}
